import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@shared/api';
import { compact, dateShort, dateTime, idr, num, relative } from '@shared/format';
import {
  CreateKeyModal,
  EditKeyModal,
  EnvBadge,
  KeyRevealModal,
  useCanEditBudgets,
  type ApiKeyRow,
  type CreatedKey,
  type ProjectOption,
} from '../components/ApiKeyBlocks';
import { ModelAccessSummary, useCatalogModels } from '../components/ModelAccess';
import { Alert, Badge, Card, Empty, Icon, InfoTip, Loading, Modal, PageHeader, Segmented } from '../components/ui';
import { BASE_URL } from '../snippets';
import '../styles/billing.css';

interface KeySpend {
  api_key_id: string;
  requests: number;
  cost_idr: number;
}

type Filter = 'active' | 'revoked' | 'all';

const STATUS_LABEL: Record<string, string> = { ACTIVE: 'Aktif', REVOKED: 'Dicabut', EXPIRED: 'Kedaluwarsa' };
const STATUS_TONE: Record<string, 'good' | 'warn' | 'neutral'> = { ACTIVE: 'good', REVOKED: 'neutral', EXPIRED: 'warn' };

function limitsOf(key: ApiKeyRow): string {
  return [
    `${num(key.rpm_limit)} request/menit`,
    `${compact(key.tpm_limit)} token/menit`,
    `${num(key.concurrency_limit)} paralel`,
    key.max_cost_per_request_idr ? `maks ${idr(key.max_cost_per_request_idr)}/request` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function noteOf(key: ApiKeyRow): string {
  const parts = [`Dibuat ${relative(key.created_at)}`];
  if (key.monthly_budget_idr) parts.push(`anggaran ${idr(key.monthly_budget_idr)}/bulan`);
  else if (key.daily_budget_idr) parts.push(`anggaran ${idr(key.daily_budget_idr)}/hari`);
  if (key.expires_at && key.status === 'ACTIVE') parts.push(`berlaku s.d. ${dateShort(key.expires_at)}`);
  return parts.join(' · ');
}

export function ApiKeys() {
  const [params, setParams] = useSearchParams();
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [spend, setSpend] = useState<Map<string, KeySpend> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(params.get('baru') === '1');
  const [revealed, setRevealed] = useState<CreatedKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);
  const [editing, setEditing] = useState<ApiKeyRow | null>(null);
  const catalog = useCatalogModels();
  const canEditBudgets = useCanEditBudgets();
  const bySlug = useMemo(() => new Map((catalog ?? []).map((m) => [m.id, m])), [catalog]);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState(params.get('q') ?? '');

  const load = async () => {
    try {
      const [k, p, s] = await Promise.all([
        api<{ data: ApiKeyRow[] }>('/v1/api-keys'),
        api<{ data: ProjectOption[] }>('/v1/projects'),
        // spend is optional
        api<{ by_api_key: KeySpend[] }>('/v1/usage/summary').catch(() => null),
      ]);
      setKeys(k.data);
      setProjects(p.data);
      setSpend(s ? new Map(s.by_api_key.map((row) => [row.api_key_id, row])) : null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const closeCreate = () => {
    setCreating(false);
    if (params.has('baru') || params.has('proyek')) setParams({}, { replace: true });
  };

  const counts = useMemo(
    () => ({
      active: keys.filter((k) => k.status === 'ACTIVE').length,
      revoked: keys.filter((k) => k.status !== 'ACTIVE').length,
      all: keys.length,
    }),
    [keys],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return keys
      .filter((k) => {
        if (filter === 'active' && k.status !== 'ACTIVE') return false;
        if (filter === 'revoked' && k.status === 'ACTIVE') return false;
        if (!q) return true;
        return k.name.toLowerCase().includes(q) || k.key_prefix.toLowerCase().includes(q) || k.project_name.toLowerCase().includes(q);
      })
      // active first, stable
      .sort((a, b) => Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE'));
  }, [keys, filter, query]);

  const totals = useMemo(() => {
    const rows = spend ? [...spend.values()] : [];
    return {
      cost: rows.reduce((sum, r) => sum + r.cost_idr, 0),
      requests: rows.reduce((sum, r) => sum + r.requests, 0),
    };
  }, [spend]);

  if (loading) return <Loading label="Memuat API key" />;

  const canCreate = projects.length > 0;
  const openCreate = () => setCreating(true);

  return (
    <>
      <PageHeader
        title="API key"
        subtitle="Buat key terpisah untuk tiap aplikasi agar biayanya mudah dilacak. Setiap key bisa dibatasi ke model tertentu."
        actions={
          <>
            <Link className="btn btn-ghost" to="/docs">
              <Icon name="book" size={16} />
              Dokumentasi
            </Link>
            <button className="btn btn-primary" onClick={openCreate} disabled={!canCreate}>
              <Icon name="plus" size={16} />
              Buat key
            </button>
          </>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}
      {!canCreate && (
        <Alert kind="warn">
          Key selalu terikat ke proyek. <Link to="/projects">Buat proyek dulu</Link>, lalu kembali ke sini.
        </Alert>
      )}

      {keys.length === 0 ? (
        <Card>
          <Empty
            icon="key"
            action={
              <div className="row">
                <button className="btn btn-primary" onClick={openCreate} disabled={!canCreate}>
                  <Icon name="plus" size={16} />
                  Buat key pertama
                </button>
                <Link className="btn" to="/docs">
                  Lihat dokumentasi
                </Link>
              </div>
            }
          >
            <div className="bl-empty-title">Belum ada API key</div>
            <p className="bl-empty-text" style={{ margin: 0 }}>
              Aplikasi Anda memakai key untuk memanggil gateway. Formatnya kompatibel dengan SDK OpenAI; cukup ganti base
              URL ke <code>{BASE_URL}</code>.
            </p>
          </Empty>
        </Card>
      ) : (
        <Card
          title="Daftar key"
          description={
            <>
              {num(counts.active)} aktif dari {num(counts.all)}
              {spend && (
                <>
                  {' · '}biaya 30 hari <strong className="bl-strong">{idr(totals.cost)}</strong> dari {num(totals.requests)} request
                </>
              )}
            </>
          }
          action={
            <div className="row wrap">
              <Segmented
                label="Filter status"
                value={filter}
                onChange={setFilter}
                options={[
                  { id: 'all', label: <>Semua <span className="muted">{counts.all}</span></> },
                  { id: 'active', label: <>Aktif <span className="muted">{counts.active}</span></> },
                  { id: 'revoked', label: <>Nonaktif <span className="muted">{counts.revoked}</span></> },
                ]}
              />
              <div className="search bl-search">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari nama, prefix, proyek" aria-label="Cari key" />
              </div>
            </div>
          }
        >
          {visible.length === 0 ? (
            <Empty icon="search">Tidak ada key yang cocok dengan filter ini.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Key</th>
                    <th>Proyek</th>
                    <th>
                      <span className="label-tip">
                        Model
                        <InfoTip label="Tentang kolom model">
                          Model yang boleh dipanggil key ini. Klik <b>Ubah</b> untuk membatasi key ke model tertentu.
                        </InfoTip>
                      </span>
                    </th>
                    <th>Terakhir dipakai</th>
                    <th className="num">Biaya 30 hari</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((key) => {
                    const used = spend?.get(key.id);
                    const active = key.status === 'ACTIVE';
                    return (
                      <tr key={key.id} className={active ? undefined : 'bl-dim'}>
                        <td style={{ maxWidth: 300, minWidth: 170 }}>
                          <div className="bl-stack" title={limitsOf(key)}>
                            <span className="bl-key-name truncate">{key.name}</span>
                            <span className="bl-sub truncate">{noteOf(key)}</span>
                          </div>
                        </td>
                        <td>
                          <span className="bl-id">
                            <span>
                              {key.key_prefix}
                              <span className="bl-mask">••••</span>
                            </span>
                          </span>
                        </td>
                        <td>
                          <div className="row" style={{ gap: 8 }}>
                            <span className="secondary">{key.project_name}</span>
                            <EnvBadge env={key.environment} />
                          </div>
                        </td>
                        <td className="nowrap">
                          <ModelAccessSummary allowed={key.allowed_models} models={bySlug} />
                        </td>
                        <td className="nowrap" title={key.last_used_at ? dateTime(key.last_used_at) : undefined}>
                          {key.last_used_at ? relative(key.last_used_at) : <span className="muted">Belum pernah</span>}
                        </td>
                        <td className="num">
                          {used ? (
                            <div className="bl-stack" style={{ justifyItems: 'end' }}>
                              <span className="bl-strong">{idr(used.cost_idr)}</span>
                              <span className="bl-sub">{num(used.requests)} request</span>
                            </div>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          <Badge tone={STATUS_TONE[key.status] ?? 'neutral'}>{STATUS_LABEL[key.status] ?? key.status}</Badge>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {active && (
                            <div className="bl-actions">
                              <button className="btn btn-sm" onClick={() => setEditing(key)} aria-label={`Ubah ${key.name}`}>
                                Ubah
                              </button>
                              <button className="btn btn-sm btn-ghost bl-revoke" onClick={() => setRevoking(key)} aria-label={`Cabut ${key.name}`}>
                                Cabut
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {creating && canCreate && (
        <CreateKeyModal
          projects={projects}
          initialProjectId={params.get('proyek') ?? undefined}
          onClose={closeCreate}
          onCreated={async (result) => {
            closeCreate();
            setRevealed(result);
            await load();
          }}
        />
      )}

      {revealed && <KeyRevealModal created={revealed} onDone={() => setRevealed(null)} />}

      {editing && (
        <EditKeyModal
          apiKey={editing}
          canEditBudgets={canEditBudgets !== false}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {revoking && (
        <RevokeModal
          apiKey={revoking}
          onClose={() => setRevoking(null)}
          onRevoked={async () => {
            setRevoking(null);
            await load();
          }}
        />
      )}
    </>
  );
}

function RevokeModal({ apiKey, onClose, onRevoked }: { apiKey: ApiKeyRow; onClose: () => void; onRevoked: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/api-keys/${apiKey.id}`, { method: 'DELETE' });
      onRevoked();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Cabut API key" onClose={onClose} width={460}>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="bl-keyline">
        <span className="bl-keyline-icon">
          <Icon name="key" size={16} />
        </span>
        <div className="bl-stack">
          <span className="bl-key-name truncate">{apiKey.name}</span>
          <span className="bl-sub">
            <span className="mono">{apiKey.key_prefix}••••</span> · {apiKey.project_name} ·{' '}
            {apiKey.last_used_at ? `dipakai ${relative(apiKey.last_used_at)}` : 'belum pernah dipakai'}
          </span>
        </div>
      </div>
      <p className="bl-text">
        Request yang memakai key ini langsung ditolak. Langkah ini tidak bisa dibatalkan; untuk memakai lagi, buat key baru.
      </p>
      <div className="bl-modal-foot">
        <button className="btn" onClick={onClose}>
          Batal
        </button>
        <button className="btn btn-danger" onClick={revoke} disabled={busy}>
          {busy && <span className="spinner" />}
          Cabut key
        </button>
      </div>
    </Modal>
  );
}
